//This file is AppBar.jsx

import React from 'react';
import { styled } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { Link, useNavigate } from 'react-router-dom';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { ThemeContext, UserContext } from 'src/store/ContextStore';
import EventIcon from '@mui/icons-material/Event';
import MenuIcon from '@mui/icons-material/Menu';

import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import axios from 'src/axios';
import logo from 'src/assets/images/trakgeneLogo.png';

import AssistantIcon from '@mui/icons-material/Assistant';
import { useCopilot } from 'src/components/VirtualAssistant/copilot/CopilotContext';

import TranslationMenu from 'src/components/settings/TranslationMenu';
import UserGroupChipCluster from './UserGroupChipCluster';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import Stack from '@mui/material/Stack';
import LicenceBanner from 'src/components/licence/LicenceBanner';
import AnnouncementsBanner from 'src/components/UI/announcements/AnnouncementsBanner';

import Badge from '@mui/material/Badge';
import Chip from '@mui/material/Chip';
import CloseIcon from '@mui/icons-material/Close';

import { Modal, Box } from '@mui/material';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import ModalHeader from 'src/components/UI/appbar/appointment-modal/ModalHeader';

import AppointmentForm2 from 'src/components/UI/appbar/appointment-modal/AppointmentForm_Notification';
import QuickActionsMenu from 'src/components/UI/appbar/QuickActionsMenu';
import NotificationBell from './NotificationBell';
import WhatsNewDialog from 'src/components/release-notes/WhatsNewDialog';
import useReleaseNotes from 'src/components/release-notes/useReleaseNotes';
import { clearWorkspace } from 'src/store/workspace/workspacePersistence';
import useUnsavedWorkGuard from 'src/components/pedigree-chart/workspace/useUnsavedWorkGuard';
import { clearExplorerState } from 'src/components/data-explorer/explorerStatePersistence';

const drawerWidth = 200;

const AppBarShift = styled(AppBar)(({ theme }) => ({
  marginLeft: drawerWidth,
  width: `calc(100% - ${drawerWidth}px)`,
  transition: `${theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  })}, background-color 1s`
}));

const Offset = styled('div', {
  shouldForwardProp: (prop) => prop !== 'offset'
})(({ offset }) => ({
  minHeight: offset
}));

const Root = styled('div')({
  flexGrow: 1
});

const Logo = styled('img')(({ theme }) => ({
  height: 40,
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const SystemMessage = styled('div')({
  // Position the wrapper itself (not each inner Alert) so the stack of
  // announcements flows naturally and clicks on the close button reach
  // their handler instead of the sibling <Toolbar> that renders after
  // this element. zIndex puts the alert above the toolbar's flex area.
  position: 'absolute',
  top: '9px',
  left: '25%',
  right: '25%',
  margin: 'auto',
  zIndex: 1
});

const PEP = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    position: 'absolute',
    left: 56
  }
}));

const SmallLogo = styled('img')(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('md')]: {
    display: 'block',
    height: 20
  }
}));

const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) =>
    !['navbar', 'navbarText', 'appearance', 'offset'].includes(prop)
})(({ theme, navbar, appearance, navbarText, offset }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: `${navbar ?? appearance.color}!important`,
  transition: 'background-color 1s',
  color: navbarText ?? appearance.text,
  borderBottom: '1px solid #c1c1c170',
  boxShadow: 'none',
  height: offset
}));

const StyledLink = styled(Link, {
  shouldForwardProp: (prop) => !['navbarText', 'appearance'].includes(prop)
})(({ theme, navbarText, appearance }) => ({
  textDecoration: 'none !important',
  fontWeight: 'bolder',
  color: navbarText ?? appearance.text,
  [theme.breakpoints.down('md')]: {
    fontSize: 16
  }
}));

export default React.memo(function CustomAppBar(props) {
  let [isModalOpen, setOpenModal] = React.useState(false);
  const handleClose = () => {
    setOpenModal(false);
  };

  let [currentModel, setCurrentModel] = React.useState({});

  const {
    theme: {
      appearance,
      navbar,
      navbarText,
      logoURL,
      logoName,
      faviconURL,
      appbarHeight,
      accent
    }
  } = React.useContext(ThemeContext);

  const offset = appbarHeight ? appbarHeight : 65;

  const { user, setUser } = React.useContext(UserContext);

  const t = useAutoTranslation();

  const navigate = useNavigate();

  const isMinimal = Boolean(props.minimal);

  const copilot = useCopilot();

  // ----------------------
  // 其他现有逻辑
  // ----------------------
  const [anchorEl, setAnchorEl] = React.useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Logging out unmounts every open pedigree and patient tab, and with them
  // any section edits still unsaved in their sidebars. It is an in-app
  // navigation, so no beforeunload prompt covers it: it asks here instead,
  // naming the tabs, and only when there is something to lose.
  const { requestLeave, leaveDialog } = useUnsavedWorkGuard();

  const logOut = () => {
    axios.get(APICONSTANTS.logout).then(() => {
      navigate(CONSTANTS.signinPath);
      setUser(null);
      localStorage.setItem('2FAloggedin', 'false');
      // Drop the open pedigree tabs (TGCLOUD-1618). sessionStorage is per browser
      // TAB and is cleared only when the tab closes, so without this the open set
      // survives into the next login here. The snapshot is owner-scoped and would
      // be refused anyway; clearing it means it is not sitting there at all.
      clearWorkspace();
      // Same reasoning for the Data Explorer's in-progress query: it is
      // owner-scoped and would be refused anyway, but a query naming a
      // signed-out user should not sit in this tab's storage at all.
      clearExplorerState();
    });
  };

  const clearSession = () => {
    setAnchorEl(null);
    requestLeave(logOut, { confirmLabel: 'Discard and log out' });
  };

  // Release notes for the build this user is on. Fetched once; the same response
  // supplies both the unread cue and the dialog's contents.
  const releaseNotes = useReleaseNotes({ enabled: Boolean(user?._id) });
  const [isWhatsNewOpen, setWhatsNewOpen] = React.useState(false);

  const openWhatsNew = () => {
    setAnchorEl(null);
    setWhatsNewOpen(true);
    releaseNotes.markSeen();
  };

  const menuId = 'primary-search-account-menu';
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      id={menuId}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      {user?.email && (
        <Typography
          variant="caption"
          style={{ paddingLeft: 15, paddingRight: 15 }}
          sx={{
            display: 'block'
          }}
        >
          {t(`Logged In as`)}:
        </Typography>
      )}
      {user?.email && (
        <Typography
          variant="caption"
          style={{ paddingLeft: 15, paddingRight: 15 }}
          sx={{
            display: 'block'
          }}
        >
          {user.email}
        </Typography>
      )}
      {props.menuItems &&
        React.cloneElement(props.menuItems, { handleMenuClose })}
      <MenuItem onClick={openWhatsNew}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            width: '100%'
          }}
        >
          {t(`What's new`)}
          {releaseNotes.unread && (
            <Box
              aria-label={t('Unread release notes')}
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: '#f59e0b',
                flexShrink: 0
              }}
            />
          )}
        </Box>
      </MenuItem>
      <MenuItem onClick={clearSession}>{t(`Log Out`)}</MenuItem>
    </Menu>
  );

  // Logged-in users always see the bundled TrakGene logo. Custom per-
  // User-Group logos are reserved for patient flows (FHQ / custom-form
  // access pages) where branding matches the issuing clinic. The gate is
  // UserContext.user: truthy = logged-in clinician/admin, null = patient
  // inside the questionnaire shell.
  const displayedLogo = !user && logoURL && logoName ? logoURL : logo;

  return (
    <>
      <StyledAppBar
        position="fixed"
        navbar={navbar}
        appearance={appearance}
        navbarText={navbarText}
        offset={offset}
      >
        <SystemMessage>
          {/* One stack for every system message, so announcements and the
              trial / licence alerts (TGCLOUD-1844) stack with the same gap
              and never overlap. LicenceBanner renders nothing for a live,
              licensed group or for a patient. */}
          <Stack spacing={1} sx={{ width: '100%' }}>
            <AnnouncementsBanner />
            {user && <LicenceBanner />}
          </Stack>
        </SystemMessage>
        <Toolbar>
          {isMinimal ? (
            <Logo alt={t('Logo')} src={displayedLogo} />
          ) : (
            <>
              <Box sx={{ display: { xs: 'inline-flex', lg: 'none' }, mr: 0.5 }}>
                <IconButton
                  onClick={props.onToggleMobileNav}
                  sx={{ color: navbarText ?? appearance.text }}
                  aria-label={t('Toggle navigation menu')}
                >
                  {props.mobileNavOpen ? <CloseIcon /> : <MenuIcon />}
                </IconButton>
              </Box>
              <Logo alt={t('Logo')} src={displayedLogo} />
              <PEP variant="h5">
                <StyledLink
                  to={CONSTANTS.adminPath}
                  appearance={appearance}
                  navbarText={navbarText}
                >
                  <SmallLogo alt="TrakGene" src={displayedLogo} />
                  {props.title ? t(props.title) : t('')}
                </StyledLink>
              </PEP>

              <Root />

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  '& .MuiIconButton-root': { padding: '6px' }
                }}
              >
                <TranslationMenu color={navbarText} />

                {/* Global quick-actions menu (icon-only bolt). Self-gates on the
                    authenticated user and the FEATURE_GLOBAL_QUICK_ACTIONS flag,
                    so it renders nothing until that flag is on. */}
                <QuickActionsMenu systemFeatures={props.systemFeatures} />

                {user && (
                  <IconButton
                    data-va-anchor="topbar.calendar"
                    aria-label={t('Calendar')}
                    onClick={() => navigate(CONSTANTS.calendarPath)}
                    color="inherit"
                    fontSize="large"
                    size="large"
                  >
                    <EventIcon />
                  </IconButton>
                )}

                {user && <NotificationBell user={user} />}

                {/* The Copilot is always available — it runs guided
                    walkthroughs with no external AI call — so the header help
                    slot always opens it. The separate tour button this replaced
                    is gone: with the Copilot always present, the welcome tour is
                    offered inside it, and two help affordances side by side just
                    made the user choose between them.

                    Nothing renders outside a CopilotProvider — the patient
                    surface — because that is where the walkthrough state and the
                    overlay live, so a launcher there could not run anything. */}
                {copilot.available ? (
                  <IconButton
                    onClick={copilot.open}
                    color="inherit"
                    size="large"
                    aria-label={t('Open Copilot assistant')}
                    data-va-anchor="topbar.copilot"
                  >
                    <AssistantIcon />
                  </IconButton>
                ) : null}

                {user && (
                  <Box sx={{ ml: 2 }}>
                    <UserGroupChipCluster
                      user={user}
                      faviconUrl={faviconURL || null}
                      navbarColor={navbar ?? appearance?.color}
                      accentColor={accent}
                      avatarSlot={
                        <IconButton
                          aria-label={t('account of current user')}
                          aria-controls={menuId}
                          aria-haspopup="true"
                          onClick={handleProfileMenuOpen}
                          color="inherit"
                          size="large"
                        >
                          {/* The unread cue has to live on the avatar, not only
                              on the menu item: a dot inside a closed menu is a
                              dot nobody sees. Same amber as the Updates tab. */}
                          <Badge
                            variant="dot"
                            invisible={!releaseNotes.unread}
                            sx={{
                              '& .MuiBadge-badge': { bgcolor: '#f59e0b' }
                            }}
                          >
                            {user.profilePhoto ? (
                              <Avatar
                                src="/api/users/profile-photo"
                                sx={{ width: 36, height: 36 }}
                              />
                            ) : (
                              <AccountCircle />
                            )}
                          </Badge>
                        </IconButton>
                      }
                    />
                  </Box>
                )}
              </Box>
            </>
          )}
        </Toolbar>
      </StyledAppBar>
      {renderMenu}
      {/* Outside the menu, which unmounts its children as it closes. */}
      {leaveDialog}
      <WhatsNewDialog
        open={isWhatsNewOpen}
        onClose={() => setWhatsNewOpen(false)}
        releases={releaseNotes.releases}
        loading={releaseNotes.loading}
      />
      <Offset offset={offset} />
      <Modal
        open={isModalOpen}
        onClose={() => setOpenModal(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <CustomDialog
          maxWidth="sm"
          title={
            <ModalHeader
              title={t('Edit Appointment')}
              // personSummary={personSummary}
            />
          }
          open={isModalOpen}
          handleClose={handleClose}
          minHeight="auto"
          onClose={true}
        >
          <AppointmentForm2 _id={currentModel.id} />
        </CustomDialog>
      </Modal>
    </>
  );
});
